import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

// The exercise tab's simple (non-copy) dialogs: video, info, item-delete, plan-delete, bulk-edit.
const ExerciseDialogs = ({
  exerciseVideoDialog,
  handleExerciseVideoClose,
  exerciseInfoDialog,
  setExerciseInfoDialog,
  exerciseDeleteDialog,
  setExerciseDeleteDialog,
  exerciseDeleteLoading,
  handleConfirmExerciseDelete,
  exercisePlanDeleteDialog,
  setExercisePlanDeleteDialog,
  exercisePlanDeleteLoading,
  handleExercisePlanDeleteConfirm,
  exerciseBulkDialog,
  exerciseBulkValues,
  setExerciseBulkValues,
  exerciseBulkError,
  exerciseBulkSaving,
  handleExerciseBulkClose,
  handleExerciseBulkSave,
}) => (
  <>
    <Dialog open={exerciseVideoDialog.open} onOpenChange={(open) => (!open ? handleExerciseVideoClose() : null)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{exerciseVideoDialog.name || 'Exercise Video'}</DialogTitle>
        </DialogHeader>
        <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
          {exerciseVideoDialog.embedUrl ? (
            <iframe
              src={exerciseVideoDialog.embedUrl}
              title={exerciseVideoDialog.name || 'Exercise video'}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : null}
        </div>
        <DialogFooter className="sm:justify-between sm:space-x-2">
          <button
            type="button"
            onClick={() => window.open(exerciseVideoDialog.rawUrl || exerciseVideoDialog.embedUrl, '_blank', 'noopener,noreferrer')}
            disabled={!exerciseVideoDialog.rawUrl && !exerciseVideoDialog.embedUrl}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Open in new tab
          </button>
          <button
            type="button"
            onClick={handleExerciseVideoClose}
            className="inline-flex items-center justify-center rounded-md bg-fitness-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-fitness-primary/90"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!exerciseInfoDialog} onOpenChange={(open) => (!open ? setExerciseInfoDialog(null) : null)}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{exerciseInfoDialog?.name || 'Exercise details'}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto whitespace-pre-wrap text-sm leading-6 text-slate-700">
          {exerciseInfoDialog?.description || 'No additional information available.'}
        </div>
        <DialogFooter className="sm:justify-end">
          <button
            type="button"
            onClick={() => setExerciseInfoDialog(null)}
            className="inline-flex items-center justify-center rounded-md bg-fitness-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-fitness-primary/90"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!exerciseDeleteDialog} onOpenChange={(open) => (!open ? setExerciseDeleteDialog(null) : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete exercise?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">This will remove the exercise from the workout plan. This action can’t be undone.</p>
          {exerciseDeleteDialog && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">{exerciseDeleteDialog.exerciseName}</div>
              <div className="text-xs text-slate-500">Plan: {exerciseDeleteDialog.planName}</div>
            </div>
          )}
        </div>
        <DialogFooter className="sm:justify-end sm:space-x-2">
          <button
            type="button"
            onClick={() => setExerciseDeleteDialog(null)}
            disabled={exerciseDeleteLoading}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmExerciseDelete}
            disabled={exerciseDeleteLoading}
            className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {exerciseDeleteLoading ? 'Deleting...' : 'Delete'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!exercisePlanDeleteDialog} onOpenChange={(open) => (!open ? setExercisePlanDeleteDialog(null) : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete workout plan?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">This will remove the workout plan and all rows in it.</p>
          {exercisePlanDeleteDialog && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">{exercisePlanDeleteDialog.name}</div>
            </div>
          )}
        </div>
        <DialogFooter className="sm:justify-end sm:space-x-2">
          <button
            type="button"
            onClick={() => setExercisePlanDeleteDialog(null)}
            disabled={exercisePlanDeleteLoading}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExercisePlanDeleteConfirm}
            disabled={exercisePlanDeleteLoading}
            className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {exercisePlanDeleteLoading ? 'Deleting...' : 'Delete'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={!!exerciseBulkDialog} onOpenChange={(open) => (!open ? handleExerciseBulkClose() : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{exerciseBulkDialog?.scope === 'session' ? 'Apply to all workouts' : 'Apply to workout'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1 text-sm">
            <span className="text-slate-600">Sets / Mode</span>
            <input
              type="text"
              value={exerciseBulkValues.sets}
              onChange={(e) => setExerciseBulkValues((prev) => ({ ...prev, sets: e.target.value }))}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-fitness-primary focus:outline-none"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-600">Reps / Intensity</span>
            <input
              type="text"
              value={exerciseBulkValues.reps}
              onChange={(e) => setExerciseBulkValues((prev) => ({ ...prev, reps: e.target.value }))}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-fitness-primary focus:outline-none"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-600">Rest / HeartRate</span>
            <input
              type="text"
              value={exerciseBulkValues.rest}
              onChange={(e) => setExerciseBulkValues((prev) => ({ ...prev, rest: e.target.value }))}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-fitness-primary focus:outline-none"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-slate-600">Speed / Duration</span>
            <input
              type="text"
              value={exerciseBulkValues.speed}
              onChange={(e) => setExerciseBulkValues((prev) => ({ ...prev, speed: e.target.value }))}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-fitness-primary focus:outline-none"
            />
          </label>
        </div>
        {exerciseBulkError && <p className="text-sm text-red-600">{exerciseBulkError}</p>}
        <DialogFooter className="sm:justify-end sm:space-x-2">
          <button
            type="button"
            onClick={handleExerciseBulkClose}
            disabled={exerciseBulkSaving}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExerciseBulkSave}
            disabled={exerciseBulkSaving}
            className="inline-flex items-center justify-center rounded-md bg-fitness-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-fitness-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {exerciseBulkSaving ? 'Saving...' : 'Apply'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
)

export default ExerciseDialogs
